import React, { useCallback, useContext, useEffect } from 'react';
import type { MessageDescriptor } from 'react-intl-next';
import { FormattedMessage, defineMessages } from 'react-intl-next';

import AutoDismissFlag from '@atlaskit/flag/auto-dismiss-flag';

import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import {
	EDIT_PAGE_PUBLISH_EXPERIENCE,
	FRONT_COVER_PUBLISH_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import {
	isCompanyHubSpaceKey,
	FrontCoverStateEnum,
} from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useIsProductAdmin } from '@confluence/current-user/entry-points/useIsProductAdmin.ts';
import { useSaveCompanyHub } from '@confluence/company-hub-utils/entry-points/useSaveCompanyHub.ts';
import {
	useChangeCompanyHubPublishState,
	GraphQLFrontCoverState,
} from '@confluence/company-hub-utils/entry-points/useChangeHubPublishState.ts';

const UNAUTHORIZED_ERROR_MESSAGE = 'Current user does not have permission to update site settings';

const i18n = defineMessages({
	publishSuccessTitle: {
		id: 'editor-features.success.front-cover.publish.title',
		description: 'Success title message when a hub is published',
		defaultMessage: '{companyHubName} is now active',
	},
	updateSuccessTitle: {
		id: 'editor-features.success.front-cover.update.title',
		description: 'Success title message when a hub is updated',
		defaultMessage: '{companyHubName} has been updated',
	},
	publishSuccessDescription: {
		id: 'editor-features.user.publish.front-cover.success',
		description: 'Success message when publishing the hub',
		defaultMessage: 'This hub is now visible to everyone. Make updates to it anytime.',
	},
	updateSuccessDescription: {
		id: 'editor-features.user.update.front-cover.success',
		description: 'Success message when updating the hub',
		defaultMessage: 'Your recent update is now visible to everyone.',
	},
	publishErrorTitle: {
		id: 'editor-features.front-cover.publish.error-title',
		description: 'Title for error flag when a user publishes in-editor but cannot enable their hub',
		defaultMessage: 'Something went wrong',
	},
	publishErrorDescription: {
		id: 'editor-features.publish.front-cover.error-message',
		description:
			'Description for error flag when a user publishes in-editor but cannot enable their hub',
		defaultMessage:
			'Due to an error, this landing page failed to publish. Reload and publish it again.',
	},
});

export const usePublishCompanyHub = (
	spaceKey: string,
	contentId: string,
	flags?: FlagsStateContainer,
	refetchNativeCollab?: (shouldCheckCache: boolean) => Promise<any>,
) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { frontCoverState, companyHubName } = useContext(SPAViewContext);
	const { isProductAdmin } = useIsProductAdmin();

	const showSuccessFlag = useCallback(() => {
		let title: MessageDescriptor;
		let description: MessageDescriptor;
		if (frontCoverState === FrontCoverStateEnum.SHOWN) {
			title = i18n.updateSuccessTitle;
			description = i18n.updateSuccessDescription;
		} else {
			title = i18n.publishSuccessTitle;
			description = i18n.publishSuccessDescription;
		}
		void flags?.showFlag({
			type: 'success-circle',
			title: <FormattedMessage {...title} values={{ companyHubName }} />,
			description: <FormattedMessage {...description} />,
			close: 'never', // XXX Works around hideAllFlags in EditorFlags!
			customComponent: AutoDismissFlag,
		});
	}, [flags, companyHubName, frontCoverState]);
	const showErrorFlag = useCallback(() => {
		void flags?.showFlag({
			type: 'error',
			title: <FormattedMessage {...i18n.publishErrorTitle} />,
			description: <FormattedMessage {...i18n.publishErrorDescription} />,
			close: 'never', // XXX Works around hideAllFlags in EditorFlags!
		});
	}, [flags]);

	const { changeHubPublishState } = useChangeCompanyHubPublishState();
	const getSuccessModalDialogData = useSaveCompanyHub();

	const publishCompanyHub = useCallback(async () => {
		const data = refetchNativeCollab ? await refetchNativeCollab(true) : {};
		const versionFromQuery = data?.content?.nodes?.[0]?.version?.number ?? 1;

		experienceTracker.start({
			name: FRONT_COVER_PUBLISH_EXPERIENCE,
			attributes: {
				contentId,
				isFirstPublish: versionFromQuery === 1,
			},
		});

		try {
			const { data } = await changeHubPublishState(GraphQLFrontCoverState.SHOWN);
			if (
				data?.updateSiteLookAndFeel.siteLookAndFeel?.frontCoverState !== FrontCoverStateEnum.SHOWN
			) {
				const isUnauthorizedError = data?.updateSiteLookAndFeel.errors?.some((error) =>
					error?.message?.includes(UNAUTHORIZED_ERROR_MESSAGE),
				);
				if (isUnauthorizedError) {
					experienceTracker.abort({
						name: FRONT_COVER_PUBLISH_EXPERIENCE,
						reason: UNAUTHORIZED_ERROR_MESSAGE,
					});
					return;
				} else {
					// if extensions is needed, use mutationErrorExtensionsAggCompat from @confluence/apollo-relay-interop
					// to convert from AGG-like response to cc-graphql-like response
					const errors = data?.updateSiteLookAndFeel.errors || [];
					const errorMessages = errors.map((error) => error.message).join('.');
					throw new Error(errorMessages || 'An unknown error occurred');
				}
			}

			experienceTracker.succeed({
				name: FRONT_COVER_PUBLISH_EXPERIENCE,
			});
		} catch (error) {
			experienceTracker.stopOnError({
				name: FRONT_COVER_PUBLISH_EXPERIENCE,
				error,
			});
			throw error;
		}
	}, [contentId, experienceTracker, changeHubPublishState, refetchNativeCollab]);

	const onPublishSuccess = useCallback(async () => {
		const isCompanyHubPublished = frontCoverState === FrontCoverStateEnum.SHOWN;

		try {
			if (!isCompanyHubPublished && isProductAdmin) {
				await publishCompanyHub();
			}
		} catch {
			showErrorFlag();
			return;
		}

		await getSuccessModalDialogData(contentId);

		showSuccessFlag();
	}, [
		frontCoverState,
		isProductAdmin,
		publishCompanyHub,
		showErrorFlag,
		showSuccessFlag,
		getSuccessModalDialogData,
		contentId,
	]);

	useEffect(() => {
		if (isCompanyHubSpaceKey(spaceKey)) {
			return experienceTracker.subscribe((event) => {
				if (
					event.name === EDIT_PAGE_PUBLISH_EXPERIENCE &&
					event.action === 'taskSuccess' &&
					event.attributes?.isCompanyHub
				) {
					void onPublishSuccess();
				}
			});
		}
	}, [contentId, experienceTracker, spaceKey, onPublishSuccess]);
};
